import React, { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import SectionGuard from '../components/SectionGuard';
import ProtectedRoute from '../components/ProtectedRoute';
import Layout from '../components/layout';

// Lazy-loaded page imports for code splitting
const HomePage = React.lazy(() => import('./Home'));
const LoginPage = React.lazy(() => import('./LoginPage'));
const CategoryPage = React.lazy(() => import('./CategoryPage'));
const LegacyRedirectPage = React.lazy(() => import('./LegacyRedirectPage'));
const ListingDetailPage = React.lazy(() => import('./ListingDetail'));
const CreateListingPage = React.lazy(() => import('./CreateListing'));
const MyListingsPage = React.lazy(() => import('./MyListingsPage'));
const DocumentsPage = React.lazy(() => import('./DocumentsPage'));
const ProfilePage = React.lazy(() => import('./ProfilePage'));
const Setup2FAPage = React.lazy(() => import('./Setup2FAPage'));
const CompleteProfilePage = React.lazy(() => import('./CompleteProfilePage'));
const AdminPage = React.lazy(() => import('./AdminPage'));
const NotificationsPage = React.lazy(() => import('./NotificationsPage'));
const PackagesPage = React.lazy(() => import('./Packages'));
const TransportPage = React.lazy(() => import('./NakliyePage'));
const TransportDetailPage = React.lazy(() => import('./NakliyeDetail'));
const CreateTransportPage = React.lazy(() => import('./CreateNakliye'));
const EditTransportPage = React.lazy(() => import('./EditNakliyePage'));
const MyTransportPage = React.lazy(() => import('./MyNakliyePage'));
const AracPage = React.lazy(() => import('./AracPage'));
const AllProfilesPage = React.lazy(() => import('./AllProfilesPage'));
const HizmetVerenPage = React.lazy(() => import('./HizmetVeren'));
const TarimTalebiPage = React.lazy(() => import('./TarimTalebiPage'));
const EventlerPage = React.lazy(() => import('./EtkinliklerPage'));
const MessagesPage = React.lazy(() => import('./MessagesPage'));
const ConversationPage = React.lazy(() => import('./ConversationPage'));
const NewConversationPage = React.lazy(() => import('./NewConversationPage'));
const RequestsimPage = React.lazy(() => import('./TaleplerimPage'));
const SatinAldiklarimPage = React.lazy(() => import('./SatinAldiklarimPage'));
const EventOlusturPage = React.lazy(() => import('./EtkinlikOlusturPage'));
const OrganizasyonTalebiPage = React.lazy(() => import('./OrganizasyonTalebiPage'));
const UlasimPage = React.lazy(() => import('./UlasimPage'));
const EmlakPage = React.lazy(() => import('./EmlakPage'));
const CreateEmlakListingPage = React.lazy(() => import('./CreateEmlakListing'));
const KiralikRequestPage = React.lazy(() => import('./KiralikTalepPage'));
const KiralikRequestsiPage = React.lazy(() => import('./KiralikTalepleriPage'));
const FavoritesPage = React.lazy(() => import('./FavoritesPage'));
const ProviderDashboardPage = React.lazy(() => import('./ProviderDashboard'));
const CreateServiceRequestPage = React.lazy(() => import('./CreateServiceRequest'));
const ServiceRequestDetailPage = React.lazy(() => import('./ServiceRequestDetail'));
const EditServiceRequestPage = React.lazy(() => import('./EditServiceRequestPage'));
const ServiceRequestsListPage = React.lazy(() => import('./ServiceRequestsListPage'));
const EditListingPage = React.lazy(() => import('./EditListingPage'));
const ServiceMarketplacePage = React.lazy(() => import('./ServiceMarketplacePage'));
const BecomeProviderPage = React.lazy(() => import('./BecomeProvider'));
const PaymentResultPage = React.lazy(() => import('./PaymentResultPage'));
const PaymentSuccessPage = React.lazy(() => import('./PaymentSuccessPage'));
const UkPackagesPage = React.lazy(() => import('./UkPackagesPage'));
const SearchResultsPage = React.lazy(() => import('./SearchResultsPage'));
const MarketPage = React.lazy(() => import('./MarketPage'));
const GalleryDealerApplyPage = React.lazy(() => import('./GalleryDealerApplyPage'));
const PriceAlertsPage = React.lazy(() => import('./PriceAlertsPage'));
const VerificationPage = React.lazy(() => import('./VerificationPage'));
const DynamicSectionPage = React.lazy(() => import('./DynamicSectionPage'));
const RealtorApplyPage = React.lazy(() => import('./RealtorApplyPage'));
const RegionSEORedirect = React.lazy(() => import('./RegionSEORedirect'));
const CreateOfferPage = React.lazy(() => import('./CreateOfferPage'));
const RentalRequestOffersPage = React.lazy(() => import('./RentalRequestOffersPage'));
const WorkPlanPage = React.lazy(() => import('./WorkPlanPage'));
const ReceivedWorkPlansPage = React.lazy(() => import('./ReceivedWorkPlansPage'));
const CorporatePage = React.lazy(() => import('./CorporatePage'));
const NasilCalisirPage = React.lazy(() => import('./NasilCalisirPage'));
const HizmetlerPage = React.lazy(() => import('./HizmetlerPage'));
const TarimHizmetleriPage = React.lazy(() => import('./TarimHizmetleriPage'));
const OtoPage = React.lazy(() => import('./OtoPage'));
const OrganizasyonPage = React.lazy(() => import('./OrganizasyonPage'));
const IsMakinesiPage = React.lazy(() => import('./IsMakinesiPage'));

// Suspense fallback skeleton
const PageFallback = () => (
  <div className="min-h-screen bg-slate-50" data-testid="page-loading-fallback">
    <div className="h-16 bg-white border-b border-slate-100 animate-pulse" />
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-4">
      <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
      <div className="h-4 w-96 bg-slate-100 rounded animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-48 bg-white rounded-xl border border-slate-100 animate-pulse" />
        ))}
      </div>
    </div>
  </div>
);

const Lazy = ({ children }) => (
  <Suspense fallback={<PageFallback />}>{children}</Suspense>
);

const L = ({ children }) => <Layout>{children}</Layout>;
const P = ({ children }) => <ProtectedRoute>{children}</ProtectedRoute>;
const PA = ({ children }) => <ProtectedRoute requireAdmin>{children}</ProtectedRoute>;
const S = ({ slug, children }) => <SectionGuard slug={slug}>{children}</SectionGuard>;

// Helper to create routes with English canonical + Turkish legacy alias (both serve same component)
const pub = (key, path, element) => <Route key={key} path={path} element={element} />;

export const publicRoutes = [
  // Home
  pub('home', '/', <L><Lazy><HomePage /></Lazy></L>),

  // Auth (English canonical + Turkish legacy aliases)
  pub('login', '/login', <Lazy><LoginPage /></Lazy>),
  pub('register', '/register', <Lazy><LoginPage defaultTab="register" /></Lazy>),
  pub('giris', '/giris', <Lazy><LoginPage /></Lazy>),
  pub('records', '/records', <Lazy><LoginPage defaultTab="register" /></Lazy>),

  // Search
  pub('search', '/search', <L><Lazy><SearchResultsPage /></Lazy></L>),
  pub('arama', '/arama', <L><Lazy><SearchResultsPage /></Lazy></L>),

  // Category & Listing detail
  pub('category', '/category/:slug', <L><Lazy><CategoryPage /></Lazy></L>),
  pub('kategori', '/kategori/:slug', <L><Lazy><CategoryPage /></Lazy></L>),
  pub('listing', '/listing/:listingId', <L><Lazy><ListingDetailPage /></Lazy></L>),
  pub('ilan', '/ilan/:listingId', <L><Lazy><ListingDetailPage /></Lazy></L>),

  // Packages / How it works
  pub('packages', '/packages', <L><Lazy><PackagesPage /></Lazy></L>),
  pub('paketler', '/paketler', <L><Lazy><PackagesPage /></Lazy></L>),
  pub('membership-plans', '/membership-plans', <L><Lazy><UkPackagesPage /></Lazy></L>),
  pub('how-it-works', '/how-it-works', <L><Lazy><NasilCalisirPage /></Lazy></L>),
  pub('nasil-calisir', '/nasil-calisir', <L><Lazy><NasilCalisirPage /></Lazy></L>),

  // Payment result
  pub('payment-result', '/payment-result', <L><Lazy><PaymentResultPage /></Lazy></L>),
  pub('payment-success', '/payment-success', <L><Lazy><PaymentSuccessPage /></Lazy></L>),
  pub('odeme-sonuc', '/odeme-sonuc', <L><Lazy><PaymentResultPage /></Lazy></L>),
  pub('odeme-basarili', '/odeme-basarili', <L><Lazy><PaymentSuccessPage /></Lazy></L>),

  // Shipping (Transport)
  pub('shipping', '/shipping', <L><Lazy><TransportPage /></Lazy></L>),
  pub('shipping-detail', '/shipping/:requestId', <L><Lazy><ServiceRequestDetailPage /></Lazy></L>),
  pub('shipping-edit', '/shipping/:requestId/edit', <L><Lazy><EditTransportPage /></Lazy></L>),
  pub('nakliye', '/nakliye', <L><Lazy><TransportPage /></Lazy></L>),
  pub('tasimacilik', '/tasimacilik', <L><Lazy><TransportPage /></Lazy></L>),
  pub('nakliye-detail', '/nakliye/:requestId', <L><Lazy><ServiceRequestDetailPage /></Lazy></L>),
  pub('nakliye-duzenle', '/nakliye/:requestId/duzenle', <L><Lazy><EditTransportPage /></Lazy></L>),

  // Vehicles (Arac)
  pub('vehicles', '/vehicles', <L><S slug="arac"><Lazy><AracPage /></Lazy></S></L>),
  pub('car-rentals', '/car-rentals', <Navigate to="/vehicles?listing_type=Rental" replace />),
  pub('arac', '/arac', <L><S slug="arac"><Lazy><AracPage /></Lazy></S></L>),
  pub('arac-kiralama', '/arac-kiralama', <Navigate to="/vehicles?listing_type=Rental" replace />),
  // SEO routes — vehicles
  pub('cars-for-sale', '/cars-for-sale', <L><S slug="arac"><Lazy><AracPage initialVehicleType="Car" initialListingType="For Sale" /></Lazy></S></L>),
  pub('cars-for-rent', '/cars-for-rent', <L><S slug="arac"><Lazy><AracPage initialVehicleType="Car" initialListingType="Rental" /></Lazy></S></L>),
  pub('motorbikes-for-sale', '/motorbikes-for-sale', <L><S slug="arac"><Lazy><AracPage initialVehicleType="Motorbike" initialListingType="For Sale" /></Lazy></S></L>),

  pub('market', '/market', <L><S slug="market"><Lazy><MarketPage /></Lazy></S></L>),

  // Jobs
  pub('jobs', '/jobs', <L><S slug="is-ilanlari"><Lazy><CategoryPage /></Lazy></S></L>),
  pub('is-ilanlari', '/is-ilanlari', <L><S slug="is-ilanlari"><Lazy><CategoryPage /></Lazy></S></L>),

  // Heavy machinery
  pub('heavy-machinery', '/heavy-machinery', <L><Lazy><IsMakinesiPage /></Lazy></L>),
  pub('is-makinesi', '/is-makinesi', <L><Lazy><IsMakinesiPage /></Lazy></L>),
  // SEO routes — Plant & Machinery
  pub('plant-machinery-for-hire', '/plant-machinery-for-hire', <L><Lazy><IsMakinesiPage /></Lazy></L>),
  pub('plant-machinery-for-sale', '/plant-machinery-for-sale', <Navigate to="/category/is-makinesi?attr_listing_type=For%20Sale" replace />),

  // SEO long-tail region-pinned URLs (e.g. /houses-for-sale/london, /flats-to-rent/manchester).
  // React Router v7 requires `:param` at the start of a segment, so the URL uses a
  // slash separator between the variant and the region slug.
  pub('seo-region-houses-for-sale', '/houses-for-sale/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="houses-for-sale" /></Lazy></L>),
  pub('seo-region-flats-for-sale', '/flats-for-sale/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="flats-for-sale" /></Lazy></L>),
  pub('seo-region-flats-to-rent', '/flats-to-rent/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="flats-to-rent" /></Lazy></L>),
  pub('seo-region-houses-to-rent', '/houses-to-rent/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="houses-to-rent" /></Lazy></L>),
  pub('seo-region-commercial-for-sale', '/commercial-property-for-sale/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="commercial-property-for-sale" /></Lazy></L>),
  pub('seo-region-commercial-to-let', '/commercial-property-to-let/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="commercial-property-to-let" /></Lazy></L>),
  pub('seo-region-cars-for-sale-region', '/cars-for-sale/:regionSlug', <L><Lazy><RegionSEORedirect variantKey="cars-for-sale" /></Lazy></L>),

  // Services
  pub('services', '/services', <L><Lazy><HizmetlerPage /></Lazy></L>),
  pub('hizmetler', '/hizmetler', <L><Lazy><HizmetlerPage /></Lazy></L>),

  pub('all-professionals', '/all-professionals', <L><Lazy><AllProfilesPage /></Lazy></L>),
  pub('tum-profesyoneller', '/tum-profesyoneller', <L><Lazy><AllProfilesPage /></Lazy></L>),

  pub('service-marketplace', '/service-marketplace', <L><Lazy><ServiceMarketplacePage /></Lazy></L>),
  pub('hizmet-pazaryeri', '/hizmet-pazaryeri', <L><Lazy><ServiceMarketplacePage /></Lazy></L>),

  pub('service-requests', '/service-requests', <L><Lazy><ServiceRequestsListPage /></Lazy></L>),
  pub('service-request-detail', '/service-requests/:requestId', <L><Lazy><ServiceRequestDetailPage /></Lazy></L>),
  pub('service-request-edit', '/service-requests/:requestId/edit', <L><Lazy><EditServiceRequestPage /></Lazy></L>),
  pub('hizmet-talepleri', '/hizmet-talepleri', <L><Lazy><ServiceRequestsListPage /></Lazy></L>),
  pub('hizmet-talep-detail', '/service-requests/:requestId', <L><Lazy><ServiceRequestDetailPage /></Lazy></L>),
  pub('hizmet-talep-duzenle', '/service-requests/:requestId/duzenle', <L><Lazy><EditServiceRequestPage /></Lazy></L>),

  pub('service-provider-profile', '/service-provider/:providerId', <L><Lazy><HizmetVerenPage /></Lazy></L>),
  pub('hizmet-veren-profil', '/provider/:providerId', <L><Lazy><HizmetVerenPage /></Lazy></L>),

  // Agriculture
  pub('agriculture-services', '/agriculture-services', <L><Lazy><TarimHizmetleriPage /></Lazy></L>),
  pub('tarim-hizmetleri', '/tarim-hizmetleri', <L><Lazy><TarimHizmetleriPage /></Lazy></L>),
  pub('agriculture', '/agriculture', <L><Lazy><CategoryPage /></Lazy></L>),
  pub('tarim', '/tarim', <L><Lazy><CategoryPage /></Lazy></L>),

  // Events
  pub('events', '/events', <L><S slug="etkinlikler"><Lazy><EventlerPage /></Lazy></S></L>),
  pub('event-detail', '/event/:listingId', <L><Lazy><ListingDetailPage /></Lazy></L>),
  pub('etkinlikler', '/etkinlikler', <L><S slug="etkinlikler"><Lazy><EventlerPage /></Lazy></S></L>),
  pub('etkinlik', '/etkinlik/:listingId', <L><Lazy><ListingDetailPage /></Lazy></L>),

  // Organisation
  pub('organisation', '/organisation', <L><Lazy><OrganizasyonPage /></Lazy></L>),
  pub('organizasyon', '/organizasyon', <L><Lazy><OrganizasyonPage /></Lazy></L>),

  // Transport
  pub('transport', '/transport', <L><Lazy><UlasimPage /></Lazy></L>),
  pub('ulasim', '/ulasim', <L><Lazy><UlasimPage /></Lazy></L>),

  // iter430: alias routes — UK/US-friendly URL variants
  pub('auto-services', '/auto-services', <L><Lazy><OtoPage /></Lazy></L>),
  pub('travel-section', '/travel', <L><Lazy><DynamicSectionPage /></Lazy></L>),
  pub('events-organisation', '/events-organisation', <L><Lazy><OrganizasyonPage /></Lazy></L>),

  // Property
  pub('property', '/property', <L><S slug="emlak"><Lazy><EmlakPage /></Lazy></S></L>),
  pub('emlak', '/emlak', <L><S slug="emlak"><Lazy><EmlakPage /></Lazy></S></L>),
  // SEO-friendly direct URLs that pre-select the property class tab
  pub('residential-property', '/residential-property', <L><S slug="emlak"><Lazy><EmlakPage /></Lazy></S></L>),
  pub('commercial-property', '/commercial-property', <L><S slug="emlak"><Lazy><EmlakPage /></Lazy></S></L>),

  // Static pages
  pub('page', '/page/:slug', <L><Lazy><CorporatePage /></Lazy></L>),
  pub('sayfa', '/sayfa/:slug', <L><Lazy><CorporatePage /></Lazy></L>),
  // UK GDPR / DPA 2018 — public legal pages (slugs map to corporate_pages collection)
  pub('privacy', '/privacy', <L><Lazy><CorporatePage /></Lazy></L>),
  pub('terms', '/terms', <L><Lazy><CorporatePage /></Lazy></L>),
  pub('cookies', '/cookies', <L><Lazy><CorporatePage /></Lazy></L>),
  pub('about', '/about', <L><Lazy><CorporatePage /></Lazy></L>),
  pub('contact', '/contact', <L><Lazy><CorporatePage /></Lazy></L>),

  // Autos (Oto)
  pub('autos', '/autos', <L><Lazy><OtoPage /></Lazy></L>),
  pub('oto', '/oto', <L><Lazy><OtoPage /></Lazy></L>),

  pub('provider-profile', '/provider/:profileId', <L><Lazy><HizmetVerenPage /></Lazy></L>),
  pub('hizmet-saglayici-profil', '/hizmet-saglayici/:profileId', <L><Lazy><HizmetVerenPage /></Lazy></L>),

  // Dynamic sections
  pub('section', '/section/:sectionSlug', <L><Lazy><DynamicSectionPage /></Lazy></L>),
  pub('bolum', '/bolum/:sectionSlug', <L><Lazy><DynamicSectionPage /></Lazy></L>),

  // Travel
  pub('travel', '/travel', <L><Lazy><DynamicSectionPage /></Lazy></L>),
  pub('seyahat', '/seyahat', <L><Lazy><DynamicSectionPage /></Lazy></L>),
];

const prot = (key, path, element) => <Route key={key} path={path} element={element} />;

export const protectedRoutes = [
  // Auto request
  prot('auto-request', '/auto-request/:serviceType', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),
  prot('oto-talebi', '/oto-talebi/:serviceType', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),

  // Dashboard redirects
  prot('dashboard-redir', '/dashboard', <P><Navigate to="/provider-dashboard" replace /></P>),
  prot('hesabim-redir', '/hesabim', <P><Navigate to="/provider-dashboard" replace /></P>),

  // Post listing
  prot('post-listing', '/post-listing', <P><L><Lazy><CreateListingPage /></Lazy></L></P>),
  prot('post-property', '/post-property', <P><L><Lazy><CreateEmlakListingPage /></Lazy></L></P>),
  prot('ilan-ver', '/ilan-ver', <P><L><Lazy><CreateListingPage /></Lazy></L></P>),
  prot('emlak-ilan-ver', '/emlak-ilan-ver', <P><L><Lazy><CreateEmlakListingPage /></Lazy></L></P>),

  // Rental requests
  prot('rental-request', '/rental-request', <P><L><Lazy><KiralikRequestPage /></Lazy></L></P>),
  prot('rental-requests', '/rental-requests', <P><L><Lazy><KiralikRequestsiPage /></Lazy></L></P>),
  prot('kiralik-talep', '/kiralik-talep', <P><L><Lazy><KiralikRequestPage /></Lazy></L></P>),
  prot('kiralik-talepler', '/kiralik-talepler', <P><L><Lazy><KiralikRequestsiPage /></Lazy></L></P>),

  // Shipping requests
  prot('shipping-request-create', '/shipping-request/create', <P><L><Lazy><CreateTransportPage /></Lazy></L></P>),
  prot('my-shipping-requests', '/my-shipping-requests', <P><L><Lazy><MyTransportPage /></Lazy></L></P>),
  prot('nakliye-talebi', '/nakliye-talebi-olustur', <P><L><Lazy><CreateTransportPage /></Lazy></L></P>),
  prot('nakliye-taleplerim', '/nakliye-taleplerim', <P><L><Lazy><MyTransportPage /></Lazy></L></P>),

  // Service requests
  prot('service-request-create', '/service-request/create', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),
  prot('service-request-typed', '/service-request/:serviceType', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),
  prot('request-service', '/request-service', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),
  prot('hizmet-talebi', '/hizmet-talebi/:serviceType', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),
  prot('hizmet-talebi-olustur', '/hizmet-talebi-olustur', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),
  prot('hizmet-talep-et', '/hizmet-talep-et', <P><L><Lazy><CreateServiceRequestPage /></Lazy></L></P>),

  // Bus ticket (legacy redirect)
  prot('otobus-bileti', '/otobus-bileti-talebi', <Navigate to="/service-request/create?section=seyahat&category=otobus-bileti" replace />),
  prot('bus-ticket', '/bus-ticket-request', <Navigate to="/service-request/create?section=seyahat&category=otobus-bileti" replace />),

  // Agriculture request
  prot('agriculture-request', '/agriculture-request/:serviceType', <P><L><Lazy><TarimTalebiPage /></Lazy></L></P>),
  prot('tarim-talebi', '/tarim-talebi/:serviceType', <P><L><Lazy><TarimTalebiPage /></Lazy></L></P>),

  // My stuff
  prot('my-listings', '/my-listings', <P><L><Lazy><MyListingsPage /></Lazy></L></P>),
  prot('my-documents', '/my-documents', <P><L><Lazy><DocumentsPage /></Lazy></L></P>),
  prot('profile', '/profile', <P><L><Lazy><ProfilePage /></Lazy></L></P>),
  prot('setup-2fa', '/setup-2fa', <P><L><Lazy><Setup2FAPage /></Lazy></L></P>),
  prot('complete-profile', '/complete-profile', <P><Lazy><CompleteProfilePage /></Lazy></P>),
  prot('price-alerts', '/price-alerts', <P><L><Lazy><PriceAlertsPage /></Lazy></L></P>),
  prot('verification', '/verification', <P><L><Lazy><VerificationPage /></Lazy></L></P>),
  prot('notifications', '/notifications', <P><L><Lazy><NotificationsPage /></Lazy></L></P>),
  prot('ilanlarim', '/ilanlarim', <P><L><Lazy><MyListingsPage /></Lazy></L></P>),
  prot('belgelerim', '/belgelerim', <P><L><Lazy><DocumentsPage /></Lazy></L></P>),
  prot('profil', '/profil', <P><L><Lazy><ProfilePage /></Lazy></L></P>),
  prot('fiyat-alarmlari', '/fiyat-alarmlari', <P><L><Lazy><PriceAlertsPage /></Lazy></L></P>),
  prot('dogrulama', '/dogrulama', <P><L><Lazy><VerificationPage /></Lazy></L></P>),
  prot('bildirimler', '/bildirimler', <P><L><Lazy><NotificationsPage /></Lazy></L></P>),

  // Messages
  prot('messages', '/messages', <P><L><Lazy><MessagesPage /></Lazy></L></P>),
  prot('messages-new', '/messages/new', <P><L><Lazy><NewConversationPage /></Lazy></L></P>),
  prot('messages-detail', '/messages/:conversationId', <P><L><Lazy><ConversationPage /></Lazy></L></P>),
  prot('mesajlar', '/mesajlar', <P><L><Lazy><MessagesPage /></Lazy></L></P>),
  prot('mesajlar-yeni', '/messages/yeni', <P><L><Lazy><NewConversationPage /></Lazy></L></P>),
  prot('mesaj-detay', '/messages/:conversationId', <P><L><Lazy><ConversationPage /></Lazy></L></P>),

  // My requests / purchases
  prot('my-requests', '/my-requests', <P><L><Lazy><RequestsimPage /></Lazy></L></P>),
  prot('my-purchases', '/my-purchases', <P><L><Lazy><SatinAldiklarimPage /></Lazy></L></P>),
  prot('taleplerim', '/taleplerim', <P><L><Lazy><RequestsimPage /></Lazy></L></P>),
  prot('satin-aldiklarim', '/satin-aldiklarim', <P><L><Lazy><SatinAldiklarimPage /></Lazy></L></P>),

  // Events
  prot('event-create', '/event/create', <P><L><Lazy><EventOlusturPage /></Lazy></L></P>),
  prot('event-edit', '/event/edit/:listingId', <P><L><Lazy><EventOlusturPage /></Lazy></L></P>),
  prot('etkinlik-olustur', '/post-event', <P><L><Lazy><EventOlusturPage /></Lazy></L></P>),
  prot('etkinlik-duzenle', '/etkinlik-duzenle/:listingId', <P><L><Lazy><EventOlusturPage /></Lazy></L></P>),

  // Organisation request
  prot('organisation-request', '/organisation-request/:serviceType', <P><L><Lazy><OrganizasyonTalebiPage /></Lazy></L></P>),
  prot('org-talebi', '/organizasyon-talebi/:serviceType', <P><L><Lazy><OrganizasyonTalebiPage /></Lazy></L></P>),

  // Favourites (UK + US-spelling alias)
  prot('favourites', '/favourites', <P><L><Lazy><FavoritesPage /></Lazy></L></P>),
  prot('favorites', '/favorites', <P><L><Lazy><FavoritesPage /></Lazy></L></P>),
  prot('favorilerim', '/favorilerim', <P><L><Lazy><FavoritesPage /></Lazy></L></P>),

  // Provider panels
  prot('provider-dashboard', '/provider-dashboard', <P><L><Lazy><ProviderDashboardPage /></Lazy></L></P>),
  prot('hizmet-saglayici', '/hizmet-saglayici', <P><L><Lazy><ProviderDashboardPage /></Lazy></L></P>),
  prot('hizmet-veren-paneli', '/hizmet-veren-paneli', <P><L><Lazy><ProviderDashboardPage /></Lazy></L></P>),

  // Edit listing
  prot('edit-listing', '/edit-listing/:listingId', <P><L><Lazy><EditListingPage /></Lazy></L></P>),
  prot('ilan-duzenle', '/ilan-duzenle/:listingId', <P><L><Lazy><EditListingPage /></Lazy></L></P>),

  // Become provider / agent / dealer
  prot('become-provider', '/become-provider', <P><L><Lazy><BecomeProviderPage /></Lazy></L></P>),
  prot('become-agent', '/become-agent', <P><L><Lazy><BecomeProviderPage /></Lazy></L></P>),
  prot('become-dealer', '/become-dealer', <P><L><Lazy><GalleryDealerApplyPage /></Lazy></L></P>),
  prot('hizmet-veren-ol', '/hizmet-veren-ol', <P><L><Lazy><BecomeProviderPage /></Lazy></L></P>),
  prot('emlakci-ol', '/emlakci-ol', <P><L><Lazy><BecomeProviderPage /></Lazy></L></P>),
  prot('galerici-ol', '/galerici-ol', <P><L><Lazy><GalleryDealerApplyPage /></Lazy></L></P>),

  // Agent / dealer dashboards (redirect to provider dashboard with tab)
  prot('agent-dashboard', '/agent-dashboard', <P><Navigate to="/provider-dashboard?tab=realtor-requests" replace /></P>),
  prot('dealer-dashboard', '/dealer-dashboard', <P><Navigate to="/provider-dashboard?tab=gallery-listings" replace /></P>),
  prot('emlakci-paneli', '/emlakci-paneli', <P><Navigate to="/provider-dashboard?tab=realtor-requests" replace /></P>),
  prot('galerici-paneli', '/galerici-paneli', <P><Navigate to="/provider-dashboard?tab=gallery-listings" replace /></P>),

  // Requests & offers
  prot('request-offers', '/requests/:requestId', <P><L><Lazy><RentalRequestOffersPage /></Lazy></L></P>),
  prot('my-request-offers', '/my-requests/:requestId', <P><L><Lazy><RentalRequestOffersPage /></Lazy></L></P>),
  prot('talep-teklifler', '/talepler/:requestId', <P><L><Lazy><RentalRequestOffersPage /></Lazy></L></P>),
  prot('taleplerim-detail', '/taleplerim/:requestId', <P><L><Lazy><RentalRequestOffersPage /></Lazy></L></P>),

  prot('offer-edit', '/offer/edit/:offerId', <P><L><Lazy><CreateOfferPage /></Lazy></L></P>),
  prot('offer-create', '/offer/create/:requestId', <P><L><Lazy><CreateOfferPage /></Lazy></L></P>),
  prot('teklif-duzenle', '/emlakci-paneli/teklif-duzenle/:offerId', <P><L><Lazy><CreateOfferPage /></Lazy></L></P>),
  prot('teklif-ver', '/emlakci-paneli/teklif-ver/:requestId', <P><L><Lazy><CreateOfferPage /></Lazy></L></P>),
  // iter436: English alias paths used by translated Link `to=` attributes
  prot('agent-dashboard-edit-offer', '/agent-dashboard/edit-offer/:offerId', <P><L><Lazy><CreateOfferPage /></Lazy></L></P>),
  prot('agent-dashboard-make-offer', '/agent-dashboard/make-offer/:requestId', <P><L><Lazy><CreateOfferPage /></Lazy></L></P>),
  prot('service-requests-detail', '/service-requests/:requestId', <P><L><Lazy><RentalRequestOffersPage /></Lazy></L></P>),

  // Work plans (with received-work-plans alias)
  prot('work-plan', '/work-plan/:offerId', <P><L><Lazy><WorkPlanPage /></Lazy></L></P>),
  prot('work-plans', '/work-plans', <P><L><Lazy><ReceivedWorkPlansPage /></Lazy></L></P>),
  prot('received-work-plans', '/received-work-plans', <P><L><Lazy><ReceivedWorkPlansPage /></Lazy></L></P>),
  prot('is-plani', '/is-plani/:offerId', <P><L><Lazy><WorkPlanPage /></Lazy></L></P>),
  prot('is-planlari', '/is-planlari', <P><L><Lazy><ReceivedWorkPlansPage /></Lazy></L></P>),

  // Dynamic catch-all for admin-created listing sections (e.g. /tarladan).
  // React Router v6 ranks static segments higher, so all explicit routes above still win.
  <Route key="dynamic-section" path="/:slug" element={<L><Lazy><CategoryPage /></Lazy></L>} />,

  // Iter508 — Legacy Düzce360 URLs (e.g. /news_show/contentid=3675-channlid=15.html)
  // that Google still indexes return blank pages otherwise. Redirect any
  // multi-segment unknown route home so the SEO crawler hits a 200 with
  // canonical link to the landing page.
  <Route key="legacy-redirect" path="*" element={<L><Lazy><LegacyRedirectPage /></Lazy></L>} />,
];

export const adminRoutes = [
  <Route key="admin" path="/admin/*" element={<PA><Lazy><AdminPage /></Lazy></PA>} />,
];
